/**
 * RFC 9457 `application/problem+json` — §7.2.
 *
 * Every error the API produces has this shape, including the ones raised by
 * Django's own handlers. What the UI needs from it is `errors[]`: each entry
 * names a `field`, which is what lets a validation failure land on the input
 * that caused it instead of in a banner at the top of the form.
 */
import { i18n } from '@/shared/i18n'

/**
 * One entry in `errors[]`.
 *
 * ⚠️ **`detail` is optional, and from 22 October 2026 the server stops sending
 * it.** The body was reformatted by product decision: the top-level `detail` is
 * the whole message — complete, actionable and already translated — and
 * `errors[]` exists for exactly one job, driving the red outline under a
 * specific input. Prose inside an entry was a second copy of a message the user
 * had already read.
 *
 * The key is **omitted entirely**, not sent empty, whenever nothing can be
 * highlighted: a non-field validation failure, an unknown or unwritable key,
 * and every 401/403/404/409/415/429/500.
 *
 * Kept optional rather than deleted because a response already in flight, or a
 * cached one, may still carry it — and because `detail` is what the field-level
 * message should use when it is there.
 */
export type ProblemFieldError = {
  field: string | null
  code: string
  detail?: string
}

/**
 * Stands in for a per-field message when the entry carries no prose.
 *
 * ⚠️ **It must be non-empty.** `Field` computes `invalid: Boolean(error)` and
 * `react-hook-form` stores whatever it is given, so an `undefined` or `''`
 * message means the input is *not* outlined — the highlighting that `errors[]`
 * now exists solely to drive would silently stop working, which is the exact
 * regression the reformatting was not supposed to cause.
 *
 * A single space rather than a sentence: the complete message is the top-level
 * `detail`, shown once above the form by `formError()`. Repeating that whole
 * composed sentence ("Base price is required, and Start time must be before
 * End time") under each of two inputs would say everything twice and attach
 * half of it to the wrong input.
 */
export const FIELD_INVALID_NO_MESSAGE = ' '

/**
 * `code` on a document this module *synthesised* rather than received.
 *
 * ⚠️ **This is how a call site tells "the server explained itself" apart from
 * "we invented a sentence because it did not".** Both end up with readable
 * prose in `detail`, so neither the text nor the status distinguishes them, and
 * the difference decides which message is better: a screen that knows it was
 * uploading a photograph can say so, and our own generic sentence cannot.
 *
 * `messageFor` reads it to prefer the caller's translated fallback; a bare
 * `formError()` ignores it and renders the generic, because a screen with no
 * fallback has nothing better and an empty error box is worse than a vague one.
 *
 * Named on the document rather than carried as a field on `ApiError` so it
 * survives the one place that reconstructs a document, and so anything logging
 * the problem records *that* it was synthesised.
 */
export const SYNTHESISED_PROBLEM_CODE = 'unexpected_response'

export type ProblemDocument = {
  type: string
  title: string
  status: number
  detail: string
  instance: string
  errors?: ProblemFieldError[]
  code?: string
  retry_after?: number
}

export class ApiError extends Error {
  readonly problem: ProblemDocument
  readonly status: number

  constructor(problem: ProblemDocument) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.problem = problem
    this.status = problem.status
  }

  /** The application-level code, where the endpoint sets one. */
  get code(): string | undefined {
    return this.problem.code
  }

  /**
   * Field errors keyed the way `react-hook-form`'s `setError` wants them.
   *
   * Entries with a null `field` are form-level and are deliberately dropped
   * here — `formError()` returns those.
   *
   * ⚠️ **Every value is guaranteed non-empty.** Since 22 October 2026 an entry
   * carries no prose, so the value falls back to `FIELD_INVALID_NO_MESSAGE` —
   * see that constant for why an empty message would silently disable the
   * outline instead of merely leaving it blank. The user-facing sentence for
   * these is the top-level `detail`, which `formError()` returns.
   */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {}
    for (const error of this.problem.errors ?? []) {
      if (error.field) result[error.field] ??= error.detail || FIELD_INVALID_NO_MESSAGE
    }
    return result
  }

  /** Whether any entry names a field — i.e. whether anything can be outlined. */
  hasFieldErrors(): boolean {
    return (this.problem.errors ?? []).some((error) => Boolean(error.field))
  }

  /**
   * The message to show above the form: the entries with no field, or the
   * problem's own `detail` when there are none.
   *
   * Without this, a 401 or a 429 — neither of which has field errors — submits
   * and appears to do nothing.
   */
  formError(): string {
    const unattached = (this.problem.errors ?? [])
      .filter((error) => !error.field)
      .map((error) => error.detail)

    return unattached.length > 0 ? unattached.join(' ') : this.message
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/** Whether a problem document is what it claims to be. */
function looksLikeProblem(value: unknown): value is ProblemDocument {
  return typeof value === 'object' && value !== null && 'status' in value && 'title' in value
}

/**
 * Whether a string body is markup rather than a sentence.
 *
 * ⚠️ **This is a guard on what a user is shown, not a parser.** It does not
 * classify HTML correctly and does not try to: it answers "would any part of
 * this body read as a tag if it reached an error banner", and it answers yes on
 * a tag-like opening found **anywhere** in the body, not only at the start.
 *
 * The anchored version it replaces claimed certainty it did not have. A body
 * that begins with prose and continues into a page — `Blocked\n<html><body>…` —
 * is exactly what a captive portal sends, and it sailed through untouched. The
 * anchor was never a property worth keeping; it was an accident of writing the
 * cheapest expression that passed the nginx cases.
 *
 * ⚠️ **The cost of searching the whole body is a wider false positive, and it
 * is accepted deliberately.** Prose containing `<3` or `a < b` is unaffected —
 * the `<` must be followed by `!`, `?`, `/` or a letter — but `5 <b` or a
 * sentence quoting a tag name would now be suppressed. A server message is
 * never written that way, and the trade runs one direction only: suppressing a
 * readable line costs the user some precision, while passing a page of markup
 * costs them the message entirely.
 *
 * ⚠️ **`?` is in the alternation and it is not hypothetical.** `<?xml …` is the
 * opening of S3's own 413 body, verbatim, and this product stores uploads in
 * S3-compatible storage (MinIO in dev). An XML or SOAP fault from a CDN or a
 * middlebox opens the same way. Without it the one error document most likely
 * to reach a user through the upload path was the one document that bypassed
 * the guard.
 *
 * ⚠️ **Sniffed from the body, not from `Content-Type`.** The headers are the
 * obvious signal and they are the wrong one: the responses this exists for come
 * from infrastructure that was never told about our contract, and a captive
 * portal or a misconfigured CDN will happily serve an HTML page labelled
 * `application/json`. The bytes are the only thing that cannot lie about what
 * will end up in the DOM.
 */
function looksLikeMarkup(body: string): boolean {
  return /<(?:[!?]|\/?[a-z])/i.test(body)
}

/**
 * Turn whatever came back into an `ApiError`.
 *
 * The fallback matters more than it looks: a 502 from nginx, a captive portal or
 * a service worker returns HTML, and an `ApiError` built from it keeps every
 * call site on one error type rather than making each one handle "the body was
 * not a problem document".
 *
 * ⚠️ **An HTML body never becomes the user's message, and that is a fixed
 * defect rather than defensive coding.** This read
 * `detail: typeof body === 'string' ? body : ''`, so *any* string body became
 * the sentence in the error banner — and uploads made that reachable. `POST
 * /api/v1/uploads/` is one multipart request through Django since 20 October
 * 2026, nginx caps the body at `client_max_body_size`, and over that cap nginx
 * answers the 413 **itself** before Django runs. The response was its stock
 * HTML page, so a user who picked a large photograph was shown
 * `<html><head><title>413 Request Entity Too Large</title>…` where the
 * explanation should have been.
 *
 * `infra/nginx/*.conf` now returns a real problem document for a 413, which
 * takes the `looksLikeProblem` path above and is the proper fix. This is the
 * other half, and it is not redundant: nginx is one proxy on one deployment,
 * while a captive portal, a corporate middlebox, a CDN error page, a service
 * worker and nginx's own 502 all answer HTML too, and none of them will ever be
 * taught our error contract. The browser is the only layer that can refuse to
 * render markup whatever sent it.
 *
 * ⚠️ **A genuine plain-text body is still passed through.** Only markup is
 * suppressed. Some gateways answer with one readable line and nothing else —
 * `Request body too large` — and replacing that with a generic sentence would
 * discard the only information in the response. Markup is suppressed because
 * markup is never a sentence; prose is kept because it might be the best thing
 * available.
 *
 * ⚠️ **Every sentence this synthesises is translated, and it has to be.**
 * `detail` and `title` are the members the SPA renders directly, so an English
 * string in either is English for an Arabic reader on a screen that is
 * otherwise entirely Arabic — the mixed-language defect CLAUDE.md records as a
 * known gap, except self-inflicted. `i18n` rather than a `useTranslation` hook
 * because this is not a component and is called from `unwrap`; `client.ts`
 * reads the active language the same way, for `Accept-Language`.
 *
 * ⚠️ **`response.statusText` is never shown to a user, and that is not caution
 * about wording.** `infra/nginx/edge-tls.conf` sets `http2 on`, and HTTP/2
 * carries no reason phrase at all — so for every real user in production
 * `statusText` is `''`. Node's `fetch` synthesises one from the status code,
 * which is why the unit suite sees "Bad Gateway" on every response and why
 * nothing here ever failed. Reading it would therefore mean showing the user a
 * hardcoded English noun in development and nothing whatsoever in production:
 * the worse of the two is the one that ships.
 *
 * It is dropped rather than kept for diagnostics, because there is nothing in it
 * to keep. A reason phrase is a restatement of `status`, which the document
 * already carries, and in production it is the empty string — so a log line
 * reading it learns strictly less than one reading `status`.
 */
export function toApiError(body: unknown, response: Response, path: string): ApiError {
  if (looksLikeProblem(body)) return new ApiError(body)

  // `trim()`, not truthiness. A body of `'   \n '` is a truthy string that is
  // not markup, so it used to become `detail` and win over everything after
  // it — including `messageFor`'s `|| fallback`, which a string of spaces
  // short-circuits. The result was the empty error box `messageFor.test.ts`
  // exists to prevent, reached by a different route.
  const prose = typeof body === 'string' ? body.trim() : ''
  const readable = prose && !looksLikeMarkup(prose) ? prose : ''

  return new ApiError({
    type: 'about:blank',
    // The translated generic, not `statusText` — see above. It lands in `title`
    // rather than `detail` so that `detail` stays an honest record of what the
    // response actually carried: empty means the server said nothing we could
    // use, and `messageFor.test.ts` asserts that directly.
    title: readable || i18n.t('error.unexpectedResponse'),
    status: response.status,
    detail: readable,
    instance: path,
    // Flagged whenever the sentence came from us rather than the server, which
    // is every branch where `readable` is empty: an empty body, a
    // whitespace-only body, and a body of markup alike. `messageFor` uses this
    // to prefer a caller's own fallback, which is the more specific message
    // where one exists. See `SYNTHESISED_PROBLEM_CODE`.
    ...(readable ? {} : { code: SYNTHESISED_PROBLEM_CODE }),
  })
}
